import React, { useState, useEffect, useCallback } from 'react';
import { X, Clock, Gift, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const OfferBanner = ({ onBookAppointment }) => {
  const [timeLeft, setTimeLeft] = useState({ hours: 6, minutes: 0, seconds: 0 });
  const [isVisible, setIsVisible] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [isReturning, setIsReturning] = useState(false);

  // Fetch session data from backend (IP-specific timer)
  const fetchSession = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tracking/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessionData(data);
        setIsReturning(data.is_returning);
        setIsExpired(data.offer_expired);
        
        // Set initial time from server
        const remainingSeconds = data.timer_remaining_seconds || 0;
        if (remainingSeconds > 0) {
          const hours = Math.floor(remainingSeconds / 3600);
          const minutes = Math.floor((remainingSeconds % 3600) / 60);
          const seconds = remainingSeconds % 60;
          setTimeLeft({ hours, minutes, seconds });
        } else {
          setIsExpired(true);
        }
        
        // Store visitor_id for tracking
        localStorage.setItem('visitor_id', data.visitor_id);
        
        // Track page view
        trackEvent('page_view', { page: window.location.pathname });
      }
    } catch (error) {
      console.error('Error fetching session:', error);
      // Fallback to 6 hours if backend fails
      setTimeLeft({ hours: 6, minutes: 0, seconds: 0 });
    }
  }, []);

  // Track events for retargeting
  const trackEvent = async (eventType, metadata = {}) => {
    try {
      await fetch(`${BACKEND_URL}/api/tracking/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: eventType,
          page: window.location.pathname,
          metadata
        })
      });
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  };

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Countdown timer
  useEffect(() => {
    if (isExpired) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        const totalSeconds = prev.hours * 3600 + prev.minutes * 60 + prev.seconds - 1;
        
        if (totalSeconds <= 0) {
          setIsExpired(true);
          return { hours: 0, minutes: 0, seconds: 0 };
        }
        
        return {
          hours: Math.floor(totalSeconds / 3600),
          minutes: Math.floor((totalSeconds % 3600) / 60),
          seconds: totalSeconds % 60
        };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isExpired]);

  const formatTime = (num) => num.toString().padStart(2, '0');

  const copyCode = () => {
    navigator.clipboard.writeText('SUCCESS20');
    trackEvent('offer_click', { action: 'copy_code' });
    alert('Discount code SUCCESS20 copied!');
  };

  const handleClaimOffer = () => {
    trackEvent('offer_click', { action: 'claim_offer' });
    if (onBookAppointment) onBookAppointment();
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Top Banner */}
      <div className={`fixed top-0 left-0 right-0 z-50 text-white shadow-lg transition-all duration-300 ${
        isExpired 
          ? 'bg-gradient-to-r from-gray-600 via-gray-700 to-gray-600' 
          : 'bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700'
      }`}>
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              {!isExpired ? (
                <>
                  <div className="hidden sm:flex items-center gap-2 animate-pulse">
                    <Sparkles className="w-5 h-5 text-yellow-300" />
                    <span className="font-bold text-yellow-300">
                      {isReturning ? "WELCOME BACK!" : "LIMITED TIME!"}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm sm:text-base">
                    <Gift className="w-5 h-5" />
                    <span className="font-semibold">Get 20% OFF</span>
                    <span className="hidden md:inline">on all plans</span>
                    <span className="mx-2">|</span>
                    <span>Code:</span>
                    <button 
                      onClick={copyCode}
                      className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full font-bold tracking-wider transition-all"
                    >
                      SUCCESS20
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 text-sm sm:text-base">
                  <span className="font-semibold">⏰ Offer Expired!</span>
                  <span className="hidden md:inline">Contact us for special pricing</span>
                </div>
              )}
            </div>
            
            {/* Timer */}
            <div className="flex items-center gap-3">
              {!isExpired && (
                <>
                  <div className="hidden sm:flex items-center gap-1 text-sm">
                    <Clock className="w-4 h-4" />
                    <span>{isReturning ? "Time left:" : "Expires in:"}</span>
                  </div>
                  <div className="flex items-center gap-1 font-mono font-bold text-lg">
                    <span className="bg-white/20 px-2 py-1 rounded">{formatTime(timeLeft.hours)}</span>
                    <span>:</span>
                    <span className="bg-white/20 px-2 py-1 rounded">{formatTime(timeLeft.minutes)}</span>
                    <span>:</span>
                    <span className="bg-white/20 px-2 py-1 rounded">{formatTime(timeLeft.seconds)}</span>
                  </div>
                </>
              )}
              
              <button 
                onClick={() => setIsVisible(false)}
                className="ml-2 p-1 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Close banner"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Floating CTA Button (Mobile) - Only show if not expired */}
      {!isExpired && (
        <div className="fixed bottom-4 left-4 right-4 z-50 sm:hidden">
          <button
            onClick={handleClaimOffer}
            className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-4 px-6 rounded-full font-bold text-lg shadow-2xl flex items-center justify-center gap-2 animate-bounce"
          >
            <Gift className="w-5 h-5" />
            Claim 20% OFF Now!
          </button>
        </div>
      )}

      {/* Spacer to prevent content from hiding under banner + header */}
      <div className="h-28"></div>
    </>
  );
};

// Export tracking functions for use in other components
export const trackFormOpen = async () => {
  try {
    await fetch(`${BACKEND_URL}/api/tracking/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: 'form_open', page: window.location.pathname })
    });
  } catch (error) {
    console.error('Error tracking form open:', error);
  }
};

export const trackFormAbandon = async () => {
  try {
    await fetch(`${BACKEND_URL}/api/tracking/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: 'form_abandon', page: window.location.pathname })
    });
  } catch (error) {
    console.error('Error tracking form abandon:', error);
  }
};

export const trackFormSubmit = async () => {
  try {
    await fetch(`${BACKEND_URL}/api/tracking/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: 'form_submit', page: window.location.pathname })
    });
  } catch (error) {
    console.error('Error tracking form submit:', error);
  }
};

export const captureLeadInfo = async (leadData) => {
  try {
    await fetch(`${BACKEND_URL}/api/tracking/capture-lead`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(leadData)
    });
  } catch (error) {
    console.error('Error capturing lead:', error);
  }
};
